import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import type { History, Location } from 'history';
import type * as Api from '@ViewModels';

/**
 * Return value from useDraftVersionState hook.
 */
interface UseDraftVersionStateReturn {
	/** Whether currently viewing the draft version */
	useDraft: boolean;
	/** Whether a draft version exists for this template */
	hasDraft: boolean;
	/** Whether a published version exists for this template */
	hasPublished: boolean;
	/** Function to toggle between draft and published versions */
	handleToggleDraft: (useDraft: boolean) => void;
}

interface UseDraftVersionStateOptions {
	/**
	 * Optional effective draft flag.
	 * This allows callers to treat descendant branch drafts as making the root
	 * automation effectively "drafted" even when the root template itself has
	 * no direct draft version.
	 */
	hasDraftOverride?: boolean;
}

/**
 * Normalize router search values so callers can safely handle histories that
 * may omit `search` on initial modal/navigation bootstrapping.
 */
function normalizeLocationSearch(locationSearch?: string | null): string {
	return locationSearch || '';
}

/**
 * Initialize useDraft from URL query parameter.
 */
function initializeUseDraftFromUrl(locationSearch?: string | null): boolean {
	const searchParams = new URLSearchParams(normalizeLocationSearch(locationSearch));
	const usedraftParam = searchParams.get('usedraft');
	return usedraftParam === 'true';
}

/**
 * Check if template versions exist.
 * IAutomationTemplateReference is returned by the query, but can contain draft/published versions.
 */
function checkTemplateVersions(template: Api.IAutomationTemplateReference): {
	draftExists: boolean;
	publishedExists: boolean;
} {
	// Type assertion is safe here because the API returns an IAutomationTemplate structure.
	const templateWithVersions = template as unknown as Api.IAutomationTemplate;
	const draft = templateWithVersions?.draft;
	const published = templateWithVersions?.published;
	const draftHasMaterializedVersion = Boolean(
		draft?.trigger || draft?.id || draft?.lastModifiedDate || (draft?.steps && draft.steps.length > 0)
	);
	const publishedExists = Boolean(
		published?.trigger || published?.id || published?.lastModifiedDate || published?.steps
	);

	// Some freshly-created templates briefly return a placeholder draft object
	// ({}, or { steps: [] }) even though the real trigger only exists on the
	// published version. Treat that placeholder as "no draft yet" when a published
	// version exists so we don't incorrectly render the synthetic Manual trigger.
	// However, for brand-new unpublished/manual templates with no published version
	// yet, we still consider the editor to be in a draft state.
	const draftExists = draftHasMaterializedVersion || !publishedExists;

	return { draftExists, publishedExists };
}

/**
 * Update URL with useDraft parameter.
 */
function updateUrlWithDraftParam(
	useDraft: boolean,
	locationSearch: string | undefined,
	history: History,
	location: Location,
	lastSetSearchRef: React.MutableRefObject<string | null>
): void {
	const searchParams = new URLSearchParams(normalizeLocationSearch(locationSearch));
	if (useDraft) {
		searchParams.set('usedraft', 'true');
	} else {
		searchParams.delete('usedraft');
	}
	const newSearch = searchParams.toString();
	lastSetSearchRef.current = newSearch;
	history.replace({ ...location, search: newSearch ? `?${newSearch}` : '' });
}

/**
 * Manages draft/published version state with URL synchronization.
 *
 * This hook provides a way to toggle between viewing draft and published versions
 * of an automation template, with the state persisted in the URL query string
 * (`?usedraft=true`).
 *
 * Key behaviors:
 * - Initializes from URL query parameter on mount
 * - Auto-routes to draft on initial load when a draft exists
 * - Detects when a draft first appears (e.g., auto-save creates one) and switches to it
 * - Syncs state with URL changes (e.g., browser back/forward)
 * - Updates URL when user toggles between versions
 *
 * @param template - The automation template reference (may be undefined while loading)
 * @returns Object with draft state and toggle function
 *
 * @example
 * ```typescript
 * const { useDraft, hasDraft, hasPublished, handleToggleDraft } = useDraftVersionState(template);
 *
 * // Use in template expansion
 * const version = useDraft ? template.draft : template.published;
 *
 * // Render toggle UI
 * <button onClick={() => handleToggleDraft(false)} disabled={!hasPublished}>Published</button>
 * <button onClick={() => handleToggleDraft(true)} disabled={!hasDraft}>Draft</button>
 * ```
 */
export function useDraftVersionState(
	template: Api.IAutomationTemplateReference | undefined,
	options: UseDraftVersionStateOptions = {}
): UseDraftVersionStateReturn {
	const history = useHistory();
	const location = useLocation();

	const [useDraft, setUseDraft] = useState(() => initializeUseDraftFromUrl(location.search));
	const [hasDraft, setHasDraft] = useState(false);
	const [hasPublished, setHasPublished] = useState(false);
	const lastSetSearchRef = useRef<string | null>(normalizeLocationSearch(location.search).replace(/^\?/, ''));
	const initialSwitchDoneRef = useRef(false);
	const prevDraftExistsRef = useRef<boolean | null>(null);

	const rootVersionState = template ? checkTemplateVersions(template) : { draftExists: false, publishedExists: false };
	const draftExists = options.hasDraftOverride ?? rootVersionState.draftExists;
	const publishedExists = rootVersionState.publishedExists;

	// Auto-route to draft on initial load, and detect when a draft first appears.
	// This also supports descendant-only drafts via hasDraftOverride.
	useEffect(() => {
		if (!template) {
			return;
		}

		setHasDraft(draftExists);
		setHasPublished(publishedExists);

		const searchParams = new URLSearchParams(normalizeLocationSearch(location.search));
		const hasUsedraftParam = searchParams.has('usedraft');

		// Case 1: Initial page load - auto-route to draft if it exists
		//         (unless user explicitly set ?usedraft param in URL)
		if (!initialSwitchDoneRef.current) {
			initialSwitchDoneRef.current = true;
			if (draftExists && !useDraft && !hasUsedraftParam) {
				setUseDraft(true);
				updateUrlWithDraftParam(true, location.search, history, location, lastSetSearchRef);
			}
		}

		// Case 2: Draft just appeared (e.g., editing published triggered auto-save
		//         which created a draft on the root or on a descendant branch)
		if (prevDraftExistsRef.current === false && draftExists && !useDraft) {
			setUseDraft(true);
			updateUrlWithDraftParam(true, location.search, history, location, lastSetSearchRef);
		}

		// Case 3: Draft was removed (e.g., user discarded changes) — switch back to published.
		// If there's no published version we leave useDraft as-is (Discard is disabled in that scenario).
		if (useDraft && !draftExists && publishedExists) {
			setUseDraft(false);
			updateUrlWithDraftParam(false, location.search, history, location, lastSetSearchRef);
		}

		prevDraftExistsRef.current = draftExists;
	}, [draftExists, history, location, location.search, publishedExists, template, useDraft]);

	// Sync useDraft with URL parameter when URL changes externally
	useEffect(() => {
		const currentSearch = normalizeLocationSearch(location.search).replace(/^\?/, '');
		if (lastSetSearchRef.current === currentSearch) {
			return;
		}

		const searchParams = new URLSearchParams(normalizeLocationSearch(location.search));
		const usedraftParam = searchParams.get('usedraft');
		const newUseDraft = usedraftParam === 'true';
		if (newUseDraft !== useDraft) {
			setUseDraft(newUseDraft);
		}
		lastSetSearchRef.current = currentSearch;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.search]);

	const handleToggleDraft = useCallback(
		(newUseDraft: boolean) => {
			setUseDraft(newUseDraft);
			updateUrlWithDraftParam(newUseDraft, location.search, history, location, lastSetSearchRef);
		},
		[history, location]
	);

	return {
		useDraft,
		hasDraft,
		hasPublished,
		handleToggleDraft,
	};
}
